import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'

interface User { id: number; full_name: string }
interface ToDo { id: number; title: string; owner: User | null; completed: boolean; priority: string; due_date: string | null; position: number; to_do_list_id: number; comment_count: number }
interface ToDoList { id: number; name: string; position: number; is_done_state: boolean; to_dos: ToDo[] }
interface Project { id: number }

interface Props {
  project: Project
  lists: ToDoList[]
  canEdit: boolean
  orgMembers: User[]
}

export default function KanbanBoard({ project, lists: serverLists, canEdit }: Props) {
  const [lists, setLists] = useState(serverLists)

  function onDragEnd(result: DropResult) {
    if (!result.destination) return
    const { source, destination, draggableId } = result
    const toDoId = Number(draggableId)
    const sourceListIdx = lists.findIndex((l) => String(l.id) === source.droppableId)
    const destListIdx = lists.findIndex((l) => String(l.id) === destination.droppableId)
    if (sourceListIdx < 0 || destListIdx < 0) return

    const newLists = lists.map((l) => ({ ...l, to_dos: [...l.to_dos] }))
    const [moved] = newLists[sourceListIdx].to_dos.splice(source.index, 1)
    newLists[destListIdx].to_dos.splice(destination.index, 0, { ...moved, to_do_list_id: newLists[destListIdx].id })
    setLists(newLists)

    router.post(
      `/projects/${project.id}/to_dos/${toDoId}/move`,
      { to_do_list_id: newLists[destListIdx].id, position: destination.index },
      { preserveScroll: true, onError: () => setLists(serverLists) }
    )
  }

  function openToDo(td: ToDo) {
    router.get(`/projects/${project.id}`, { tab: 'kanban', to_do: td.id }, { preserveState: true, preserveScroll: true })
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {lists.map((list) => (
          <Droppable droppableId={String(list.id)} key={list.id}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="bg-gray-100 rounded-lg p-3 w-72 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-text-primary text-sm">{list.name} <span className="text-xs text-text-muted">({list.to_dos.length})</span></h3>
                </div>
                <div className="space-y-2 min-h-[40px]">
                  {list.to_dos.map((td, idx) => (
                    <Draggable draggableId={String(td.id)} index={idx} key={td.id} isDragDisabled={!canEdit}>
                      {(p) => (
                        <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} onClick={() => openToDo(td)} className={`border rounded p-2 text-sm cursor-pointer hover:border-[#ED1C24] ${td.completed ? 'bg-[#F5F5F5]' : 'bg-white'}`}>
                          <div className={`font-medium text-text-primary ${td.completed ? 'line-through' : ''}`}>{td.title}</div>
                          <div className="flex items-center justify-between text-xs text-text-muted mt-1">
                            <span>{td.owner?.full_name || '—'}</span>
                            <span className="flex items-center gap-2">
                              {td.due_date && <span>{new Date(td.due_date).toLocaleDateString()}</span>}
                              {td.comment_count > 0 && <span>💬 {td.comment_count}</span>}
                            </span>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
                {canEdit && <InlineAddToDo projectId={project.id} listId={list.id} />}
              </div>
            )}
          </Droppable>
        ))}
        {canEdit && <AddListButton projectId={project.id} />}
      </div>
    </DragDropContext>
  )
}

function InlineAddToDo({ projectId, listId }: { projectId: number; listId: number }) {
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    router.post(`/projects/${projectId}/to_dos`, { to_do_list_id: listId, to_do: { title } }, { onSuccess: () => { setTitle(''); setAdding(false) } })
  }
  if (!adding) return <button onClick={() => setAdding(true)} className="w-full text-left text-xs text-text-muted mt-2 hover:text-text-primary">+ Add to-do</button>
  return (
    <form onSubmit={submit} className="mt-2">
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => { if (!title) setAdding(false) }} placeholder="To-do title" className="w-full border rounded px-2 py-1 text-sm" />
    </form>
  )
}

function AddListButton({ projectId }: { projectId: number }) {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    router.post(`/projects/${projectId}/to_do_lists`, { name }, { onSuccess: () => { setName(''); setAdding(false) } })
  }
  if (!adding) return <button onClick={() => setAdding(true)} className="text-sm text-text-muted self-start mt-3 px-3 py-2">+ Add list</button>
  return (
    <form onSubmit={submit} className="bg-gray-100 rounded-lg p-3 w-72">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="List name" className="w-full border rounded px-2 py-1 text-sm" />
    </form>
  )
}
